import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './se-event-registration.css.lit';

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The SeEventRegistation component
 * @element se-event-registration
 */
@customElement('se-event-registration')
export class SeEventRegistation extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'event-type' })
  eventType: string;

  @property({ type: String, attribute: 'event-format' })
  eventFormat: string;

  @property({ type: String, attribute: 'hide-event-format' })
  hideEventFormat: string;

  @property({ type: String, attribute: 'start-date' })
  startDate: string;

  @property({ type: String, attribute: 'end-date' })
  endDate: string;

  @property({ type: Boolean, attribute: 'all-day' })
  allDay: boolean;

  @property({ type: String, attribute: 'time-zone' })
  timeZone: string;

  @property({ type: String, attribute: 'address-line-1' })
  addressLine1: string;

  @property({ type: String, attribute: 'address-line-2' })
  addressLine2: string;

  @property({ type: String, attribute: 'zip-code' })
  zipCode: string;

  @property({ type: String, attribute: 'region' })
  region: string;

  @property({ type: String, attribute: 'registration-link' })
  registrationLink: string;

  @property({ type: String, attribute: 'remote-link-text' })
  remoteLinkText: string;

  @property({ type: String, attribute: 'custom-event-link' })
  customEventLink: string;

  @property({ type: String, attribute: 'remote-link' })
  remoteLink: string;

  addressLink() {
    const address1 = this.addressLine1
      ? this.addressLine1.replace(' ', '+')
      : null;
    const address2 = this.addressLine2
      ? this.addressLine2.replace(' ', '+')
      : null;
    const reg = this.region ? this.region.replace(' ', '+') : null;
    const zip = this.zipCode ? this.zipCode.replace(' ', '+') : null;
    return `https://www.google.com/maps?q=${address1 ? address1 + '+' : ''}${
      address2 ? address2 + '+' : ''
    }${reg ? reg + '+' : ''}${zip ? zip : null}`;
  }

  getHour(date: Date | undefined) {
    let hour = date?.getHours();
    const minutes = date?.getMinutes();
    let ampm = 'am';
    if (hour && hour >= 12) {
      hour = hour - 12;
      ampm = 'pm';
    }
    return `${hour !== 0 ? hour : 12}:${
      (minutes || minutes === 0) && minutes < 10
        ? '0' + minutes.toString()
        : minutes
    }${ampm}`;
  }

  getDateValues(startDate: Date | undefined, endDate: Date | undefined): string {
    if (!startDate) return '';

    const startDay = startDate.getDate();
    const endDay = endDate?.getDate();
    const startMonth = startDate.getMonth();
    const endMonth = endDate?.getMonth();
    const startYear = startDate.getFullYear();
    const endYear = endDate?.getFullYear();
    
    if (
      !endDate ||
      (startDay === endDay && startMonth === endMonth && startYear === startYear)
    ) {
      // single day
      return `${monthNames[startMonth]} ${startDay}, ${startYear}`;
    }

    // multi-day, same month & year
    if (startYear === endYear && startMonth === endMonth) {
      return `${monthNames[startMonth]} ${startDay} - ${endDay}, ${startYear}`;
    }

    // multi-day, diff month, same year
    if (startYear === endYear && startMonth !== endMonth) {
      return `${monthNames[startMonth]} ${startDay} - ${monthNames[endMonth!]} ${endDay}, ${startYear}`;
    }

    // multi-day, diff month & year
    return `${monthNames[startMonth]} ${startDay}, ${startYear} - ${monthNames[endMonth!]} ${endDay}, ${endYear}`;
  }

  render(): TemplateResult {
    const startDate = new Date(this.startDate);
    const endDate =
      this.endDate && this.startDate !== this.endDate
        ? new Date(this.endDate)
        : undefined;

    const formattedDate = this.getDateValues(startDate, endDate);
    const showEventFormat = this.hideEventFormat === 'false';

    return html`<div class="se-event-registration">
      ${showEventFormat && this.eventFormat
        ? html`<div class="se-event-registration__format">${this.eventFormat}</div>`
        : ''}
      <div class="se-event-registration__type">${this.eventType}</div>
      ${startDate &&
      html`<div class="se-event-registration__date">${formattedDate}</div>`}
      
      ${!this.allDay
        ? html`<div class="se-event-registration__time">
            ${startDate && this.getHour(startDate)}
            ${startDate && endDate ? `-` : ''}
            ${endDate && this.getHour(endDate)} ${this.timeZone}
          </div>`
        : ''}
      ${this.addressLine1 && this.region && this.zipCode
        ? html`<div class="se-event-registration__address">
            <a
              href="${this.addressLink()}"
              target="_blank"
              rel="noopener noreferrer"
            >
              ${this.addressLine1}
              ${this.addressLine2 &&
              html` <br />
                ${this.addressLine2}`}
              ${this.region || this.zipCode
                ? html` <br />
                    ${this.region} ${this.zipCode}`
                : ''}
            </a>
          </div>`
        : ''}
      ${this.registrationButton()}
      ${this.remoteLink ? this.remoteLinkButton() : ''}
      ${this.customLinkButton()} 
    </div>`;
  }

  registrationButton() {
    const now = new Date();
    const startDate = new Date(this.startDate);
    const endDate = this.endDate && 
    this.startDate !== this.endDate 
    ? new Date(this.endDate) 
    : null;

    const isWithinEventPeriod = startDate &&
        (now < startDate ||
         (now >= startDate && 
          (!endDate || 
            now <= new Date(endDate.getTime() + 24 * 60 * 60 * 1000))));

    if (this.registrationLink && isWithinEventPeriod) {
        return html`
            <se-button
                button-url="${this.registrationLink}"
                variant="registration"
                size="medium"
            >
                <outline-icon
                    slot="right"
                    size="1em"
                    name="arrow-right"
                    library="system"
                    color="brand-secondary-1"
                ></outline-icon>
                Register
            </se-button>
        `;
    }

    return null;
}
  
  remoteLinkButton() {
    return this.remoteLink
      ? html`
          <se-button
            button-url="${this.remoteLink}"
            variant="primary-2"
            size="small"
            class="se-event-registration_remote-link-btn"
          >
            <outline-icon
              slot="right"
              size="1em"
              name="arrow-right"
              library="system"
              color="brand-secondary-1"
            ></outline-icon>
            ${this.remoteLinkText ?? "Access"}
          </se-button>
        `
      : null;
  }

  customLinkButton() {
    return this.customEventLink
      ? html`
          <se-button
            button-url="${this.customEventLink}"
            variant="primary-2"
            size="small"
            class="se-event-registration_remote-link-btn"
          >
            <outline-icon
              slot="right"
              size="1em"
              name="arrow-right"
              library="system"
              color="brand-secondary-1"
            ></outline-icon>
            CLE Verification
          </se-button>
        `
      : null;
  }
}
